<script lang="ts" module>
  export type IllustrationKind = 'welcome' | 'work' | 'week' | 'period' | 'round' | 'client' | 'notif' | 'done';
</script>

<script lang="ts">
  // Small brand illustrations for the introduction steps. Pure SVG coloured
  // with design tokens, so they follow the accent colour and dark mode.
  import { t } from '../i18n.svelte';
  import { prefs } from '../prefs.svelte';

  let { kind, color = null, initials = '', size = 240 }: { kind: IllustrationKind; color?: string | null; initials?: string; size?: number } = $props();

  const symbol = $derived({ EUR: '€', USD: '$', GBP: '£', CHF: 'Fr' }[prefs.value.currency] ?? '€');
  const uid = Math.random().toString(36).slice(2, 8);
  const BIRD = 'M150.959,225.324c-5.969,-8.94 -6.235,-8.137 -14.807,-11.08c-137.376,-47.171 -146.926,-227.665 -129.789,-213.449c177.259,147.043 250.63,96.731 378.4,222.907c20.234,19.982 26.814,-63.739 22.499,-82.963c-6.778,-30.2 -19.967,-38.979 -20.8,-41.142c-1.78,-4.616 3.82,5.491 48.827,-13.59c47.307,-20.056 47.358,9.273 97.311,8.832c8.488,-0.075 0.655,4.628 -4.829,12.327c-31.412,44.099 -3.834,131.659 6.891,120.644c135.727,-139.397 168.517,-58.882 380.179,-226.073c10.246,-8.093 5.988,73.339 -24.386,124.982c-43.72,74.333 -117.083,85.979 -117.858,98.975c-24.518,3.515 -138.816,45.615 -188.898,93.414c-121.998,116.438 -118.413,283.664 -117.404,321.018c-0.071,1.439 -4.48,90.438 -8.452,14.355c-0.059,-1.123 -0.089,-1.036 -1.2,-13.981c8.161,-37.761 -11.087,-232.588 -124.61,-328.279c-62.784,-52.923 -177.326,-86.202 -181.073,-86.895Z';

  const weekBars = [62, 78, 54, 86, 70, 14, 8];
  const confetti = [
    { x: 54, y: 46, r: -20, c: 'c1', d: 0 }, { x: 186, y: 40, r: 30, c: 'c2', d: 80 }, { x: 70, y: 132, r: 45, c: 'c3', d: 160 },
    { x: 176, y: 138, r: -35, c: 'c4', d: 40 }, { x: 40, y: 92, r: 10, c: 'c5', d: 120 }, { x: 202, y: 90, r: -60, c: 'c1', d: 200 },
    { x: 96, y: 30, r: 70, c: 'c4', d: 100 }, { x: 150, y: 24, r: -10, c: 'c3', d: 20 }, { x: 120, y: 156, r: 25, c: 'c2', d: 140 },
    { x: 214, y: 120, r: 50, c: 'c5', d: 60 }, { x: 28, y: 128, r: -45, c: 'c2', d: 180 }, { x: 138, y: 150, r: 80, c: 'c1', d: 220 }
  ];
</script>

<svg class="ill {kind}" width={size} height={size * 0.75} viewBox="0 0 240 180" role="img" aria-hidden="true">
  <defs>
    <linearGradient id="peak-{uid}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" style="stop-color: var(--bird-1)" />
      <stop offset="0.5" style="stop-color: var(--bird-3)" />
      <stop offset="1" style="stop-color: var(--brand)" />
    </linearGradient>
    <linearGradient id="bar-{uid}" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" style="stop-color: var(--brand-400)" />
      <stop offset="1" style="stop-color: var(--brand)" />
    </linearGradient>
    <linearGradient id="bird-{uid}" x1="0" y1="0" x2="1" y2="1">
      <stop offset="0" style="stop-color: var(--bird-1)" />
      <stop offset="0.45" style="stop-color: var(--bird-2)" />
      <stop offset="1" style="stop-color: var(--bird-4)" />
    </linearGradient>
    <filter id="shadow-{uid}" x="-20%" y="-20%" width="140%" height="160%">
      <feDropShadow dx="0" dy="6" stdDeviation="7" flood-opacity="0.14" />
    </filter>
  </defs>

  {#if kind === 'welcome'}
    <circle class="soft" cx="120" cy="96" r="74" />
    <circle class="accent sun anim" cx="170" cy="58" r="13" />
    <path fill="url(#peak-{uid})" d="M24 156 L92 64 L126 108 L148 84 L216 156 Z" />
    <path class="snow" d="M92 64 L104 80 L97 77 L91 83 L85 76 L80 80 Z" />
    <path class="snow" d="M148 84 L157 96 L151 94 L146 99 L141 95 Z" />
    <rect class="ground" x="18" y="154" width="204" height="4" rx="2" />
    <g class="float anim">
      <path transform="translate(44 30) scale(0.062)" fill="url(#bird-{uid})" d={BIRD} />
    </g>
    <g class="badge-clock pop anim" filter="url(#shadow-{uid})">
      <circle class="card" cx="188" cy="126" r="20" />
      <circle class="brand" cx="188" cy="126" r="2.6" />
      <line class="hand hand-m anim" x1="188" y1="126" x2="188" y2="113" />
      <line class="hand" x1="188" y1="126" x2="197" y2="126" />
    </g>
  {:else if kind === 'work'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <g filter="url(#shadow-{uid})">
      <rect class="card" x="44" y="42" width="152" height="98" rx="16" />
    </g>
    <circle class="soft-strong" cx="70" cy="66" r="10" />
    <rect class="line" x="88" y="61" width="58" height="6" rx="3" />
    <rect class="muted" x="88" y="71" width="36" height="5" rx="2.5" />
    <text class="big" x="60" y="112">{symbol}85</text>
    <text class="unit" x="124" y="112">{t('common.per_hour')}</text>
    <rect class="brand" x="146" y="98" width="34" height="18" rx="9" />
    <circle class="white knob anim" cx="171" cy="107" r="6.5" />
    <g class="float anim">
      <circle class="dot-light" cx="198" cy="40" r="9" />
      <circle class="dot-dark" cx="214" cy="58" r="9" />
      <circle class="accent" cx="196" cy="70" r="7" />
    </g>
    <path class="spark anim" d="M34 120 l3 -8 l3 8 l8 3 l-8 3 l-3 8 l-3 -8 l-8 -3 Z" />
  {:else if kind === 'week'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <line class="target" x1="34" y1="70" x2="206" y2="70" />
    {#each weekBars as h, i (i)}
      <rect
        class="bar anim {i > 4 ? 'weekend' : ''}"
        style="animation-delay: {i * 60}ms"
        x={40 + i * 24}
        y={140 - h}
        width="16"
        height={h}
        rx="5"
        fill={i > 4 ? undefined : `url(#bar-${uid})`}
      />
    {/each}
    <rect class="ground" x="32" y="140" width="176" height="3" rx="1.5" />
    <g class="pop anim" filter="url(#shadow-{uid})">
      <circle class="card" cx="196" cy="40" r="17" />
      <path class="check-stroke" d="M188 40 l6 6 l10 -12" />
    </g>
  {:else if kind === 'period'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <g filter="url(#shadow-{uid})">
      <rect class="card" x="48" y="34" width="144" height="122" rx="16" />
    </g>
    <path class="brand" d="M48 50 a16 16 0 0 1 16 -16 h112 a16 16 0 0 1 16 16 v10 h-144 Z" />
    <rect class="ring" x="80" y="26" width="6" height="16" rx="3" />
    <rect class="ring" x="154" y="26" width="6" height="16" rx="3" />
    {#each Array.from({ length: 28 }, (_, i) => i) as i (i)}
      {@const col = i % 7}
      {@const row = Math.floor(i / 7)}
      {@const inRange = i >= 3 && i <= 16}
      {@const edge = i === 3 || i === 16}
      <rect
        class={edge ? 'brand cell anim' : inRange ? 'range cell anim' : 'muted'}
        style={inRange ? `animation-delay: ${(i - 3) * 35}ms` : undefined}
        x={60 + col * 18}
        y={72 + row * 19}
        width="13"
        height="13"
        rx="4"
      />
    {/each}
  {:else if kind === 'round'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <g filter="url(#shadow-{uid})">
      <circle class="card" cx="120" cy="92" r="56" />
    </g>
    {#each [0, 90, 180, 270] as deg (deg)}
      <rect class="tick-major" x="118" y="40" width="4" height="10" rx="2" transform="rotate({deg} 120 92)" />
    {/each}
    {#each [30, 60, 120, 150, 210, 240, 300, 330] as deg (deg)}
      <rect class="tick-minor" x="119" y="41" width="2" height="6" rx="1" transform="rotate({deg} 120 92)" />
    {/each}
    <path class="arc anim" d="M120 56 A36 36 0 0 1 156 92" />
    <g class="sweep anim">
      <line class="hand" x1="120" y1="92" x2="120" y2="60" />
    </g>
    <circle class="brand" cx="120" cy="92" r="4" />
    <g class="pop anim">
      <rect class="brand" x="160" y="122" width="54" height="24" rx="12" />
      <text class="pill-text" x="187" y="138">+15</text>
    </g>
  {:else if kind === 'client'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <g filter="url(#shadow-{uid})" class="float-slow anim">
      <rect class="card" x="34" y="44" width="172" height="100" rx="16" />
      <rect x="50" y="60" width="38" height="38" rx="11" style="fill: {color || 'var(--brand)'}; transition: fill 200ms" />
      <text class="initials" x="69" y="85">{initials || 'TP'}</text>
      <rect class="line" x="100" y="64" width="80" height="7" rx="3.5" />
      <rect class="muted" x="100" y="77" width="54" height="6" rx="3" />
      <rect class="muted" x="100" y="88" width="66" height="6" rx="3" />
      <rect class="chip" x="50" y="114" width="44" height="16" rx="8" />
      <rect class="chip" x="100" y="114" width="36" height="16" rx="8" />
      <rect class="chip" x="142" y="114" width="48" height="16" rx="8" />
    </g>
    <g class="pop anim">
      <circle class="brand" cx="200" cy="44" r="14" />
      <path class="plus" d="M200 37 v14 M193 44 h14" />
    </g>
  {:else if kind === 'notif'}
    <circle class="soft" cx="120" cy="92" r="72" />
    <rect class="card" x="82" y="20" width="76" height="148" rx="18" />
    <rect class="muted" x="106" y="28" width="28" height="6" rx="3" />
    <rect class="muted" x="92" y="112" width="56" height="6" rx="3" />
    <rect class="muted" x="92" y="124" width="40" height="6" rx="3" />
    <g class="slide anim" filter="url(#shadow-{uid})">
      <rect class="card" x="48" y="56" width="144" height="44" rx="12" />
      <circle class="brand" cx="70" cy="78" r="11" />
      <path class="bell" d="M65.5 81 v-4.5 a4.5 4.5 0 0 1 9 0 v4.5 l1.5 1.5 h-12 Z M68.5 84.5 a1.6 1.6 0 0 0 3 0" />
      <rect class="line" x="88" y="69" width="78" height="6" rx="3" />
      <rect class="muted" x="88" y="81" width="56" height="5" rx="2.5" />
    </g>
    <path class="wave anim" d="M186 44 a14 14 0 0 1 0 18" />
    <path class="wave w2 anim" d="M193 38 a22 22 0 0 1 0 30" />
  {:else if kind === 'done'}
    <circle class="soft" cx="120" cy="90" r="74" />
    {#each confetti as c, i (i)}
      <rect class="confetti {c.c} anim" style="animation-delay: {c.d}ms; transform-origin: {c.x}px {c.y}px" x={c.x - 4} y={c.y - 2} width="8" height="4" rx="1.5" transform="rotate({c.r} {c.x} {c.y})" />
    {/each}
    <g class="pop anim">
      <circle class="brand" cx="120" cy="90" r="42" />
      <circle class="ring-soft" cx="120" cy="90" r="50" />
      <path class="check-big anim" d="M101 91 l13 13 l26 -28" />
    </g>
  {/if}
</svg>

<style>
  .ill { display: block; max-width: 100%; height: auto; overflow: visible; }

  .soft { fill: var(--brand-soft); }
  .soft-strong { fill: color-mix(in srgb, var(--brand) 30%, transparent); }
  .card { fill: var(--surface); stroke: var(--border); stroke-width: 1.5; }
  .brand { fill: var(--brand); }
  .accent { fill: var(--accent); }
  .muted { fill: var(--surface-3); }
  .line { fill: var(--border-strong); }
  .white { fill: var(--surface); }
  .snow { fill: var(--surface); opacity: 0.9; }
  .ground { fill: color-mix(in srgb, var(--brand) 25%, transparent); }
  .hand { stroke: var(--text); stroke-width: 2.5; stroke-linecap: round; }
  .big { font: 800 30px var(--font-sans); fill: var(--text); letter-spacing: -0.03em; }
  .unit { font: 600 14px var(--font-sans); fill: var(--text-3); }
  .dot-light { fill: var(--surface); stroke: var(--border-strong); stroke-width: 1.5; }
  .dot-dark { fill: var(--text); }
  .spark { fill: var(--warning); }
  .target { stroke: var(--accent); stroke-width: 2; stroke-dasharray: 5 5; }
  .weekend { fill: var(--chart-muted); }
  .check-stroke { fill: none; stroke: var(--success); stroke-width: 3.5; stroke-linecap: round; stroke-linejoin: round; }
  .ring { fill: var(--text-3); }
  .range { fill: color-mix(in srgb, var(--brand) 28%, transparent); }
  .tick-major { fill: var(--text-2); }
  .tick-minor { fill: var(--border-strong); }
  .arc { fill: none; stroke: var(--brand); stroke-width: 9; stroke-linecap: round; opacity: 0.85; }
  .pill-text { font: 700 12px var(--font-sans); fill: var(--on-brand); text-anchor: middle; }
  .initials { font: 700 14px var(--font-sans); fill: var(--on-brand); text-anchor: middle; }
  .chip { fill: var(--brand-soft); }
  .plus { stroke: var(--on-brand); stroke-width: 3; stroke-linecap: round; }
  .bell { fill: var(--on-brand); }
  .wave { fill: none; stroke: var(--brand); stroke-width: 3; stroke-linecap: round; }
  .ring-soft { fill: none; stroke: color-mix(in srgb, var(--brand) 30%, transparent); stroke-width: 3; }
  .check-big { fill: none; stroke: var(--on-brand); stroke-width: 8; stroke-linecap: round; stroke-linejoin: round; stroke-dasharray: 70; stroke-dashoffset: 70; }
  .confetti.c1 { fill: var(--brand); }
  .confetti.c2 { fill: var(--accent); }
  .confetti.c3 { fill: var(--warning); }
  .confetti.c4 { fill: var(--success); }
  .confetti.c5 { fill: var(--chart-6); }

  /* Motion */
  .float { animation: float 4.5s ease-in-out infinite; }
  .float-slow { animation: float 6s ease-in-out infinite; }
  .sun { transform-box: fill-box; transform-origin: center; animation: pulse 3.2s ease-in-out infinite; }
  .pop { transform-box: fill-box; transform-origin: center; animation: pop 520ms cubic-bezier(0.34, 1.56, 0.64, 1) both 180ms; }
  .hand-m { transform-origin: 188px 126px; animation: spin 6s linear infinite; }
  .knob { animation: knob 2.8s ease-in-out infinite; }
  .spark { transform-box: fill-box; transform-origin: center; animation: twinkle 2.4s ease-in-out infinite; }
  .bar { transform-box: fill-box; transform-origin: bottom; animation: grow 560ms var(--ease) both; }
  .cell.anim { transform-box: fill-box; transform-origin: center; animation: pop 360ms var(--ease) both; }
  .arc { stroke-dasharray: 57; stroke-dashoffset: 57; animation: draw 900ms var(--ease) forwards 200ms; }
  .sweep { transform-origin: 120px 92px; animation: sweep 900ms var(--ease) forwards 200ms; }
  .slide { animation: slide 620ms cubic-bezier(0.34, 1.4, 0.64, 1) both 150ms; }
  .wave { animation: wave 1.8s ease-out infinite; }
  .wave.w2 { animation-delay: 300ms; }
  .check-big { animation: draw 520ms var(--ease) forwards 420ms; }
  .confetti { animation: confetti 900ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }

  @keyframes float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
  @keyframes pulse { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.12); } }
  @keyframes pop { from { opacity: 0; transform: scale(0.4); } to { opacity: 1; transform: scale(1); } }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes knob { 0%, 40% { transform: translateX(0); } 50%, 90% { transform: translateX(-16px); } 100% { transform: translateX(0); } }
  @keyframes twinkle { 0%, 100% { transform: scale(0.8) rotate(0); opacity: 0.6; } 50% { transform: scale(1.1) rotate(20deg); opacity: 1; } }
  @keyframes grow { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  @keyframes draw { to { stroke-dashoffset: 0; } }
  @keyframes sweep { from { transform: rotate(0deg); } to { transform: rotate(90deg); } }
  @keyframes slide { from { opacity: 0; transform: translateY(-24px); } to { opacity: 1; transform: none; } }
  @keyframes wave { 0% { opacity: 0; } 30% { opacity: 1; } 100% { opacity: 0; } }
  @keyframes confetti { 0% { opacity: 0; transform: translate(0, 18px) scale(0.3); } 60% { opacity: 1; } 100% { opacity: 1; transform: none; } }

  :global([data-motion='reduced']) .ill .anim { animation: none; }
  :global([data-motion='reduced']) .arc,
  :global([data-motion='reduced']) .check-big { stroke-dashoffset: 0; }
  :global([data-motion='reduced']) .sweep { transform: rotate(90deg); }
  @media (prefers-reduced-motion: reduce) {
    .ill .anim { animation: none; }
    .arc, .check-big { stroke-dashoffset: 0; }
    .sweep { transform: rotate(90deg); }
  }
</style>
